import { styled } from '@mui/material';
import MUITypography, { TypographyProps } from '@mui/material/Typography';

export type MuiTypographyProps = TypographyProps;

export const Typography = MUITypography;

export interface FlashyTypographyProps extends MuiTypographyProps {
	/** Optional text color for the animation; defaults to theme.palette.text.primary */
	$textColor?: string;
	/** The animation type to use: 'pulse' (default) or 'scroll'. */
	$animation?: 'pulse' | 'scroll';
}

/**
 * A typography component with an occasional scrolling flash of colour.
 */
export const FlashyTypography = styled(Typography)<FlashyTypographyProps>(({ theme, $textColor, $animation = 'pulse' }) => {
	const resolvedTextColor = $textColor || theme.palette.text.primary;

	// Base styles common to both animations
	const baseStyles = {
		backgroundClip: 'text',
		WebkitBackgroundClip: 'text',
		color: 'transparent'
	};

	if ($animation === 'scroll') {
		return {
			...baseStyles,
			backgroundImage: `linear-gradient(
				90deg,
				${resolvedTextColor} 0%,
				${resolvedTextColor} 45%,
				#1F458B 50%,
				#2F68D1 51%,
				#FF72A8 53%,
				#FF9D43 56%,
				#FF72A8 60%,
				#2F68D1 62%,
				#1F458B 63%,
				${resolvedTextColor} 67%,
				${resolvedTextColor} 100%
			)`,
			backgroundSize: '300% 100%',
			animation: 'scroll 3s linear infinite',
			'@keyframes scroll': {
				'0%': {
					backgroundPosition: '100% 0'
				},
				'100%': {
					backgroundPosition: '0% 0'
				}
			}
		};
	} else {
		// Original pulse animation
		return {
			...baseStyles,
			backgroundImage: `radial-gradient(
				circle, 
				#1F458B 0%, 
				#2F68D1 20%, 
				#FF72A8 35%,
				#FF9D43 40%,
				#FF9D43 70%,
				#FF72A8 75%, 
				#2F68D1 80%,
				#1F458B 100% 
			)`,
			backgroundSize: '200% 500%',
			animation: 'pulse 10s ease infinite alternate',
			'@keyframes pulse': {
				'0%, 45%': {
					backgroundPosition: '0% 100%',
					color: resolvedTextColor
				},
				'50%, 52%': {
					color: 'transparent'
				},
				'53.5%': {
					color: 'transparent',
					filter: 'drop-shadow(0 0 0.75px #FF72A8)'
				},
				'54%': {
					color: 'transparent',
					filter: 'drop-shadow(0 0 1px #FF9D43)'
				},
				'57%': {
					color: 'transparent',
					filter: 'drop-shadow(0 0 1px #FF9D43)'
				},
				'57.5%': {
					color: 'transparent',
					filter: 'drop-shadow(0 0 0.75px #FF72A8)'
				},
				'58%, 60%': {
					color: 'transparent'
				},
				'65%, 100%': {
					backgroundPosition: '100% 0%',
					color: resolvedTextColor
				}
			}
		};
	}
});
